// ============================================================================
// CHANGO V5.0 — "site" SPA entry (Task #53)
// ============================================================================
// Public marketing site. Served from www.chango.local for every path that
// isn't a capability URL (see entries/public.jsx for /s/ and /m/).
//
// Main public pages:
//   /          → Landing
//   /pricing   → Pricing
//   /product   → Product
//   /about     → About
//
// Remaining routes are still CMS-driven:
//   /matrix    → SitePage       (slug "matrix")
//   /cases     → SitePage       (slug "cases")
//   /help      → SitePage       (slug "help")
//   /blog      → SitePage       (slug "blog")
//   /contact   → SitePage       (slug "contact")
//
// What this entry deliberately does NOT import:
//   • AuthContext / RequireAuth / RoleGate   — no session on marketing
//   • AppShell / QuoteWizard / Billing       — tenant surface, different host
//   • Ops console                            — admin surface, different host
//
// The goal is bundle size: a cold visitor on https://www.chango.local/
// should download only what's needed to render a CMS page. Everything else
// lives in sibling SPAs.
//
// Auth links ("Log in" / "Register") in the marketing chrome are hard-coded
// to app.chango.local — they cross the host boundary instead of staying
// in-SPA. The SitePageRenderer knows to rewrite relative /login hrefs to
// https://app.chango.local/login at build time.
// ============================================================================

import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

import '../styles/tailwind.css';

import { initTelemetry, captureError } from '../lib/telemetry.js';
initTelemetry().catch(() => { /* no-op */ });

if (typeof window !== 'undefined') {
  window.addEventListener('error', (ev) => {
    try { captureError(ev.error || new Error(ev.message || 'window.error'), { kind: 'window.error', spa: 'site' }); } catch {}
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev?.reason;
    try { captureError(reason instanceof Error ? reason : new Error(String(reason)), { kind: 'unhandledrejection', spa: 'site' }); } catch {}
  });
}

import Landing from '../pages/site/Landing.jsx';
import Pricing from '../pages/site/Pricing.jsx';
import Product from '../pages/site/Product.jsx';
import About from '../pages/site/About.jsx';
import SitePage from '../pages/site/SitePage.jsx';
import NotFound from '../pages/NotFound.jsx';
import { useParams } from 'react-router-dom';

// Task #58 — /legal/:slug wrapper. Prefixes the CMS slug with `legal-` so
// Ops can publish `legal-terms` / `legal-privacy` / `legal-billing-terms`
// through the standard CMS editor without colliding with top-level slugs.
function LegalPage() {
  const { slug } = useParams();
  const cmsSlug = slug ? `legal-${slug}` : 'legal-terms';
  return <SitePage slug={cmsSlug} />;
}

function SiteApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/"         element={<Landing />} />
        <Route path="/pricing"  element={<Pricing />} />
        <Route path="/product"  element={<Product />} />
        <Route path="/matrix"   element={<SitePage slug="matrix" />} />
        <Route path="/cases"    element={<SitePage slug="cases" />} />
        <Route path="/help"     element={<SitePage slug="help" />} />
        <Route path="/blog"     element={<SitePage slug="blog" />} />
        <Route path="/about"    element={<About />} />
        <Route path="/contact"  element={<SitePage slug="contact" />} />
        {/* Legal pages (Task #58). The slug is forwarded to SitePageRenderer
            which fetches /api/site/pages/legal-:slug. Ops can publish these
            via the CMS editor (legal-terms, legal-privacy). */}
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="*"         element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <SiteApp />
  </React.StrictMode>
);
